import dom from "../../../dist/jsx-render/dom";
import JSXComponent from "../../../dist/jsx-render/JSXComponent";
import { TreeNodeModel } from "./Models/TreeNodeModel";
import { TreeNodeComponent } from "./NodeComponents/TreeNodeComponent";
import { ITreeViewModelConverter } from "./Models/Interfaces/ITreeViewModelConverter";
import { ButtonComponent } from "../ButtonComponent";
import { Constants } from "../../Common/constants";
import { app } from "../../Frame/App";
import { ITreeViewFilter } from "./Modules/Filter/ITreeViewFilter";
import { ITreeNodeModel } from "./Models/Interfaces/ITreeNodeModel";
import { CheckedState } from "./Models/enums/CheckedState";
import { DynPopup } from "../DynPopup";
import { SaveBehavior } from "../../Areas/Admin/Shared/Models/AttributesCategory";

export type TreeViewProps = {
    businessModel?: any;
    childrenProperty?: string;
    displayProperty?: string;
    colorProperty?: string;
    //displayPropertyIsLanguageString?: boolean;
    allowDragDrop?: boolean;

    isDebug?: boolean;

    allowSelect?: boolean;
    includeAncestorsInSelected?: boolean;
    selectedIds?: number[];
    checkboxCheckedEvent?: (node: ITreeNodeModel, event: Event) => void;
    selectNodesIndependently?: boolean;

    dropTargetClass?: string;
    dragDropBusinessModelAction?: (draggedModel: any, droppedOnModel: any) => void;

    /** insert drag element on same level and not as child */
    dropOnSameLevel?: boolean;

    /** used to create tree model when drag source is another control */
    createTreeNodeModelOnDrop?: (elementId: string) => ITreeNodeModel;

    insertOnTop?: boolean;
    hasIsDeleted?: boolean;

    childrenCollapsed?: boolean;

    model?: ITreeNodeModel[];
    modelConverter?: ITreeViewModelConverter;
    filter?: ITreeViewFilter;

    displayLevels?: number;
    internalComponentType?: any;
    groupHeaderComponentType?: any;

    externalConfirmDeleteAction?: (node: TreeNodeComponent) => boolean;
    nodeClickAction?: (node: TreeNodeComponent, event: MouseEvent) => void;
    afterAddAction?: () => void;
    afterDeleteAction?: () => void;
    preFilterAction?: () => void;

    internalProps?: any;
    groupHeaderInternalProps?: any;

    searchTerm?: string;

    /** If this property is set, lazy loading is activated and the data is displayed
     * in chunks of the given number. 
     * The user can still filter over all existing nodes. */
    lazyLoadingAmount?: number;
    loadChildrenOnExpand?: boolean;

    /** To deal with huge data sets, activate this function. Childnodes of the next level are then created when a node is expanded.
     * Use with care, this might break other functionality. */

    hugeDataSetOptimization?: boolean;

    parentProperty?: string;
    levelProperty?: string;

    preventDragOnNodesWithChildren?: boolean;

    isDisabled?: boolean;

    preventFullWidth?: boolean;
}

export class TreeView extends JSXComponent {
    constructor(props: TreeViewProps) { super(props); }
    props: TreeViewProps;

    /**
     * Index of last element retrieved by lazy loading.
     * */
    lazyLoadingIndex: number = 0;
    //private loadChildrenOnExpand: boolean = false;

    element: HTMLElement;
    nodes: TreeNodeComponent[] = [];
    flatModel: ITreeNodeModel[];
    filteredTreeViewRef: TreeView;

    public addRootNode(businessModel: any) {
        let model = new TreeNodeModel();
        model.businessModel = businessModel;
        model.children = [];

        this.appendRootNode(model);
    }

    public appendRootNode(model: TreeNodeModel) {
        let firstNode = this.element.firstChild.firstChild;
        if (firstNode && this.props.insertOnTop) {
            this.element.firstChild.insertBefore(this.getTreeNodeComponent(model), firstNode);
        } else {
            this.element.firstChild.appendChild(this.getTreeNodeComponent(model));
        }
    }

    public insertRootNode(model: TreeNodeModel, insertBefore: TreeNodeComponent) {
        // add the html elements
        if (insertBefore && insertBefore.element) {
            this.element.firstChild.insertBefore(this.getTreeNodeComponent(model, insertBefore), insertBefore.element);
        } else {
            this.element.firstChild.appendChild(this.getTreeNodeComponent(model, insertBefore));
        }

        if (!this.flatModel.includes(model)) this.flatModel.push(model);
    }

    // Remove a node from the TreeView
    public removeNode(model: any) {
        // Get the tree node component from the TreeView's list of nodes by business model.
        var nodeComponent = this.nodes.find(n => n.props.model.businessModel == model);

        if (!nodeComponent) return;

        // Remove node from parent.
        if (nodeComponent.parentNode) {
            // If the node has parent, then remove node from it's parent.
            nodeComponent.parentNode.removeChild(nodeComponent);

            // If expander is present, and all child nodes have been removed, also remove expander.
            if (nodeComponent.parentNode.childNodes.length == 0) {
                nodeComponent.parentNode.removeExpander();
            }
        }
        else {
            // If the node has no parent, remove node from TreeView nodes.
            this.nodes.splice(this.nodes.indexOf(nodeComponent), 1);
        }

        // Remove the html element.
        if (this.element.firstChild.contains(nodeComponent.element)) {
            this.element.firstChild.removeChild(nodeComponent.element);
        }

        // Remove model from flatModel.
        if (this.flatModel.includes(nodeComponent.props.model)) {
            this.flatModel.splice(this.flatModel.indexOf(nodeComponent.props.model), 1);
        }
    }

    public appendRootNodeComponent = (node: TreeNodeComponent, first?: boolean) => {
        let nodesElement = this.element.querySelector(".nodes");

        if (first === true) {
            let firstChild = nodesElement.firstChild;
            nodesElement.insertBefore(node.element, firstChild);
        } else {
            nodesElement.appendChild(node.element);
        }
    }

    public getTreeNodeComponent(model: TreeNodeModel, insertBeforeNode?: TreeNodeComponent): HTMLElement {
        return <TreeNodeComponent
            model={model}
            level={0}
            treeViewParent={this}
            internalComponentType={this.props.internalComponentType}
            internalProps={this.props.internalProps}
            groupHeaderComponentType={this.props.groupHeaderComponentType}
            groupHeaderInternalProps={this.props.groupHeaderInternalProps}
            externalConfirmDeleteAction={this.props.externalConfirmDeleteAction}
            nodeClickAction={this.props.nodeClickAction}
            childrenCollapsed={this.props.childrenCollapsed}
            searchTerm={this.props.searchTerm}
            afterAddAction={this.props.afterAddAction}
            afterDeleteAction={this.props.afterDeleteAction}
            allowDragDrop={this.props.allowDragDrop}
            dropTargetClass={this.props.dropTargetClass}
            dragDropBusinessModelAction={this.props.dragDropBusinessModelAction}
            insertOnTop={this.props.insertOnTop}
            allowSelect={this.props.allowSelect}
            dropOnSameLevel={this.props.dropOnSameLevel}
            createTreeNodeModelOnDrop={this.props.createTreeNodeModelOnDrop}
            checkboxCheckedEvent={this.props.checkboxCheckedEvent}
            loadChildrenOnExpand={this.props.loadChildrenOnExpand}
            preventDragOnNodesWithChildren={this.props.preventDragOnNodesWithChildren}
            insertBeforeNode={insertBeforeNode}
            preventFullWidth={this.props.preventFullWidth}
        />;
    }

    createNodes(): HTMLElement[] {
        if (this.props.loadChildrenOnExpand) {
            // set all node on levels > 0 to visible = false
            for (let node of this.props.model) {
                for (let childNode of node.children) {
                    this.setVisibilityRecursive(childNode, false);
                }
            }
        }

        let visibleModel = this.props.model.filter(n => n.visible === true);

        if (this.props.lazyLoadingAmount) {

            // activate lazy loading
            if (this.props.model.length > this.props.lazyLoadingAmount) {
                this.lazyLoadingIndex = this.lazyLoadingIndex + this.props.lazyLoadingAmount;

                return visibleModel
                    .slice(0, this.props.lazyLoadingAmount)
                    .map(m => this.getTreeNodeComponent(m))
            }
        }

        this.disableLoadMoreButton();
        return visibleModel
            .map(m => this.getTreeNodeComponent(m))
    }

    private setVisibilityRecursive = (node: ITreeNodeModel, visible: boolean) => {
        node.visible = visible;

        if (node.children) {
            for (let childNode of node.children) {
                this.setVisibilityRecursive(childNode, visible);
            }
        }
    }

    lastSearchTerm: string;
    filterParentElement: HTMLElement;
    filterTree(searchTerm: string, level: number, maxLength: number = 3): void {
        if (!this.filterParentElement) {
            this.filterParentElement = this.element.parentElement;
        }

        if (searchTerm != this.lastSearchTerm) {
            this.lastSearchTerm = searchTerm;
        } else {
            return;
        }

        if ((this.props.filter || this.props.modelConverter) && searchTerm.length >= maxLength) {

            if (this.props.preFilterAction) { this.props.preFilterAction(); }

            //let filteredModel: ITreeNodeModel[];
            if (this.props.filter) {
                this.props.filter
                    .filterModel(this.flatModel, searchTerm, true);
            } else if (this.props.modelConverter) {

                // flatmodel!!!
                //filteredModel = this.props.modelConverter
                //    .getFilteredTreeViewModel(searchTerm, this.props.businessModel, this.props.displayLevels);
            } else {
                throw new Error("No filter set. Implement it either in a TreeViewFilter class or the model converter.");
            }

            app.destroyDomElements(this.element);
            if (this.filteredTreeViewElement) {
                this.destroyFilteredTreeView(this.filteredTreeViewElement);
            }

            // Append the filtered nodes to the DOM.
            this.filterParentElement.appendChild(this.createFilteredTreeView(searchTerm));
        }
        else {
            if (this.filteredTreeViewElement) {
                if (this.props.preFilterAction) { this.props.preFilterAction(); }

                //if (this.props.allowSelect) {
                //    this.props.selectedIds = this.filteredTreeViewRef.props.selectedIds;
                //    this.props.includeAncestorsInSelected = this.filteredTreeViewRef.props.includeAncestorsInSelected;
                //}
                this.destroyFilteredTreeView(this.filteredTreeViewElement);

                // reset visiblity to true for all nodes
                this.flatModel.forEach(n => n.visible = true);

                // Show unfiltered tree nodes again.
                this.filterParentElement.appendChild(this.render());
            }
        }
    }

    filteredTreeViewElement: HTMLElement;
    createFilteredTreeView(searchTerm: string): HTMLElement {
        let filteredTreeViewProps = {
            childrenProperty: this.props.childrenProperty,
            displayProperty: this.props.displayProperty,
            colorProperty: this.props.colorProperty,
            childrenCollapsed: this.props.childrenCollapsed,
            allowDragDrop: this.props.allowDragDrop,
            levelProperty: this.props.levelProperty,


            allowSelect: this.props.allowSelect,
            selectedIds: this.props.selectedIds,
            includeAncestorsInSelected: this.props.includeAncestorsInSelected,

            dropTargetClass: this.props.dropTargetClass,
            dragDropBusinessModelAction: this.props.dragDropBusinessModelAction,
            filter: this.props.filter,

            model: this.props.model,

            displayLevels: this.props.displayLevels,
            internalComponentType: this.props.internalComponentType,
            groupHeaderComponentType: this.props.groupHeaderComponentType,

            externalConfirmDeleteAction: this.props.externalConfirmDeleteAction,
            nodeClickAction: this.props.nodeClickAction,
            afterAddAction: this.props.afterAddAction,
            afterDeleteAction: this.props.afterDeleteAction,

            internalProps: this.props.internalProps,
            groupHeaderInternalProps: this.props.groupHeaderInternalProps,
            lazyLoadingAmount: this.props.lazyLoadingAmount,
            searchTerm: searchTerm,
            insertOnTop: this.props.insertOnTop,
        }

        this.filteredTreeViewRef = new TreeView({ ...filteredTreeViewProps });

        this.filteredTreeViewElement = this.filteredTreeViewRef.render();
        return this.filteredTreeViewElement;
    }

    destroyFilteredTreeView(parent: HTMLElement) {
        // TODO app.destroyDomElements
        app.destroyDomElements(this.filteredTreeViewElement);
        //parent.removeChild(this.filteredTreeViewElement);
        this.filteredTreeViewElement = null;
        this.filteredTreeViewRef = null;
    }

    expandAll = () => {
        if (this.flatModel.length <= 2000) {
            this.expandAllNodes();
        } else {
            DynPopup.ShowDialog(Resources.Common.infoCategoryEditExpandAllDisabled);
        }
    }

    //**Expand all nodes of current tree view without size check*/
    public forceExpandAll = () => {
        this.expandAllNodes();
    }

    //**Functionality to expand all nodes of current tree view*/
    private expandAllNodes = () => {
        if (this.filteredTreeViewRef) {
            this.filteredTreeViewRef.nodes.forEach(n => n.expand());
        } else {
            this.nodes.forEach(n => n.expand());
        }
    }

    collapseAll = () => {
        if (this.filteredTreeViewRef) {
            this.filteredTreeViewRef.nodes.forEach(n => n.collapse());
        } else {
            this.nodes.forEach(n => n.collapse());
        }
    }

    public removeSelection = () => {
        // This is done in two steps, because when loadChildrenOnExpand is true, not all models might have a respective node.
        this.removeSelectionFromModels();
        this.removeSelectionFromNodes();
    }

    removeSelectionFromNodes = () => {
        for (let model of this.flatModel) {
            model.selected = CheckedState.Unchecked;
        }
    }

    removeSelectionFromModels = () => {
        for (let node of this.nodes) {
            node.unselect(true, false);
        }
    }

    public selectIds = (ids: number[]) => {
        for (let id of ids) {
            this.selectId(id);
        }
    }

    public selectId = (id: number) => {
        if (this.props.selectedIds && this.props.selectedIds.indexOf(id) == -1) {
            this.props.selectedIds.push(id);
        }

        let model = this.flatModel.find(m => m.businessModel?.id == id);
        if (model) {
            this.setSelected(model);
        }
    }

    public selectByName = (name: string) => {
        let model = this.flatModel.find(m => m.businessModel[this.props.displayProperty] == name);
        if (model) {
            model.selected = CheckedState.Checked;

            let id = model.businessModel.id;
            if (id) {
                let node = this.nodes.find(n => n.props.model.businessModel?.id == id);
                if (node) {
                    node.select(true, true);
                }
            }
        }
    }

    public getSelectedNames = (): string[] => {
        let names = [];
        for (let id of this.props.selectedIds) {
            let model = this.flatModel.find(m => m.businessModel?.id == id);
            if (model) {
                let name = model.getDisplayName();
                if (name) {
                    names.push(name);
                }
            }
        }
        return names;
    }

    public getSelectedModels = () => {
        let models: ITreeNodeModel[] = [];
        for (let id of this.props.selectedIds) {
            let model = this.flatModel.find(m => m.businessModel?.id == id);
            if (model) {
                models.push(model);
            }
        }
        return models;
    }

    public getSelectedAndPartiallySelectedModels = () => {
        let models = this.flatModel.filter(m => m.selected == CheckedState.Checked || m.selected == CheckedState.Indeterminate);
        return models;
    }

    public getAllSelectedModels = () => {
        let models = this.flatModel.filter(m => m.selected == CheckedState.Checked);
        return models;
    }

    removeNodeElementsAfterLevel = (level: number) => {
        // remove nodes on next level
        let nodesToDelete = this.element.firstElementChild.querySelectorAll(".tree-node-wrapper.level-" + (level + 1));
        if (nodesToDelete.length > 0) {
            nodesToDelete.forEach(e => e.parentElement.removeChild(e));
        }

        // remove expanders on this level
        let expandersToDelete = this.element.firstElementChild.querySelectorAll(".tree-node-wrapper.level-" + level + " .tree-node-expander");
        if (expandersToDelete.length > 0) {
            expandersToDelete.forEach(e => e.parentElement.removeChild(e));
        }
    }

    private loadMoreElements = () => {

        if (!this.props.lazyLoadingAmount) {
            // Do nothing.. Lazy loading is deactivated, the button to load more elements should be disabled.
            return;
        }

        if (this.props.model.length > this.lazyLoadingIndex + this.props.lazyLoadingAmount) {
            //console.log("Aktueller Index: " + this.lazyLoadingIndex + ", Menge: " + this.props.lazyLoadingAmount);
            //console.log(this.props.model);

            let slice = this.props.model.slice(this.lazyLoadingIndex, this.lazyLoadingIndex + this.props.lazyLoadingAmount);

            for (let i: number = 0; i < (slice.length); i++) {
                this.element.firstChild.appendChild(this.getTreeNodeComponent(slice[i]));
            }

            this.lazyLoadingIndex = this.lazyLoadingIndex + this.props.lazyLoadingAmount;
        } else {
            this.props.model
                .slice(this.lazyLoadingIndex)
                .map(m => this.element.firstChild.appendChild(this.getTreeNodeComponent(m)));

            this.disableLoadMoreButton();
        }
    }

    private disableLoadMoreButton() {
        this.buttonComponent.classList.add(Constants.cssClass_hidden);
    }

    buttonComponent: HTMLElement;
    getLoadMoreButton(): HTMLElement {
        return <ButtonComponent text={Resources.Common.loadMoreElements + "..."} action={this.loadMoreElements} />;
    }

    getModel(): TreeNodeModel[] {
        let model: TreeNodeModel[] = [];
        if (!this.flatModel) this.flatModel = [];

        if (Array.isArray(this.props.businessModel)) {
            for (let entry of this.props.businessModel) {
                this.getModelFirstLevel(model, entry);
            }
        } else {
            this.getModelFirstLevel(model, this.props.businessModel);
        }

        return model;
    }

    getModelFirstLevel(model: TreeNodeModel[], businessModel: any) {
        let node = new TreeNodeModel();
        node.displayProperty = this.props.displayProperty;
        node.colorProperty = this.props.colorProperty;
        node.businessModel = businessModel;
        node.visible = true;
        node.level = 0;
        businessModel[this.props.levelProperty] = 0;
        this.setSelected(node);

        this.getModelRecursive(businessModel, node, 1);

        model.push(node);
        this.flatModel.push(node);
    }

    getModelRecursive(businessModelItem: any, parentNode: TreeNodeModel, level: number) {
        let nodeChildren: TreeNodeModel[] = [];

        if (businessModelItem[this.props.childrenProperty]) {
            for (let child of businessModelItem[this.props.childrenProperty]) {
                if (this.props.hasIsDeleted !== false && child.isDeleted === true) {
                    continue;
                }

                let childNode = new TreeNodeModel();
                childNode.parent = parentNode;
                childNode.displayProperty = this.props.displayProperty;
                childNode.colorProperty = this.props.colorProperty;
                childNode.businessModel = child;
                this.setSelected(childNode);
                childNode.level = level;
                businessModelItem[this.props.levelProperty] = level;

                this.getModelRecursive(child, childNode, level + 1);

                nodeChildren.push(childNode);
                this.flatModel.push(childNode);
            }
        }

        parentNode.children = nodeChildren;
    }

    flattenModel = () => {
        this.flatModel = [];

        let addToFlat = (model: ITreeNodeModel) => {
            this.flatModel.push(model);

            if (model.children) {
                for (let subModel of model.children) {
                    addToFlat(subModel);
                }
            }
        }

        for (let model of this.props.model) {
            addToFlat(model);
        }

    }

    setSelected = (node: ITreeNodeModel) => {
        if (!this.props.selectNodesIndependently && node.parent && node.parent.selected == CheckedState.Checked) {
            node.selected = CheckedState.Checked;
        } else if (this.props.selectedIds) {
            let hasId: boolean = false;
            if (typeof (node.businessModel.id) == "string") {
                // Try parse to number
                let intId = Number(node.businessModel.id);
                if (!isNaN(intId) && this.props.selectedIds.includes(intId)) {
                    hasId = true;
                }
            } else {
                if (this.props.selectedIds.includes(node.businessModel.id))
                    hasId = true;
            }

            let existingNodeComponent = this.nodes.find(n => n.props.model == node);

            if (hasId) {
                if (this.props.includeAncestorsInSelected || !node.children?.length) {
                    if (existingNodeComponent) {
                        existingNodeComponent.select(true, true);
                    } else {
                        node.selected = CheckedState.Checked;

                        if (!this.props.selectNodesIndependently) {
                            var currentNode = node;
                            while (currentNode.parent) {
                                let existingParentComponent = this.nodes.find(n => n.props.model == currentNode.parent);
                                if (existingParentComponent) {
                                    existingParentComponent.checkboxApplyChildrenState();
                                } else {
                                    currentNode.parent.selected = CheckedState.Indeterminate;
                                }

                                currentNode = currentNode.parent;
                            }
                        }
                    }
                }
            }
        }
    }

    render() {
        this.buttonComponent = this.getLoadMoreButton();
        if (!this.props.selectedIds) this.props.selectedIds = [];

        this.validate();

        if (this.props.modelConverter) {
            this.props.model = this.props.modelConverter.convertModel(
                this.props.businessModel, this.props.selectedIds);
            this.flatModel = this.props.modelConverter.getFlatNodes();
        } else if (!this.props.model) {
            this.props.model = this.getModel();
        }

        if (this.props.model && !this.flatModel) {
            this.flattenModel();
        }

        for (let model of this.flatModel) {
            model.displayProperty = this.props.displayProperty;
            model.colorProperty = this.props.colorProperty;
        }

        if (this.props.hugeDataSetOptimization && this.flatModel.length > 1000) {
            this.props.loadChildrenOnExpand = true;
        }

        this.element = <div className="responsive-tree-view">
            <div className="nodes">
                {...this.createNodes()}
            </div>
            {this.props.lazyLoadingAmount && this.props.model.length > this.props.lazyLoadingAmount
                ? <div className="tree-view-buttons">
                    {this.buttonComponent}
                </div>
                : []}
            {this.props.allowSelect
                ? <input type="hidden" className="selected-ids" />
                : []}
            {this.props.isDebug
                ? <ButtonComponent action={this.logModel} text="Log Model" />
                : []}
        </div>;

        return this.element;
    }

    logModel = () => {
        console.log(this.props.model);
    }

    private validate() {
        if (!this.props.model && !this.props.businessModel) {
            throw new Error("Insufficient props set - you have to provide either a model or a businessModel.");
        }
    }
}

